import React, { useMemo, useRef, useState } from "react";
import { Form, FormInstance, Table, TableProps } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import searchIconWhite from "../../../../assets/images/icons/search-icon-white.svg";
import { useGetAllInvoicesQuery } from "../../../../api/queries";
import { IInvoiceForTable } from "../../../../models/Invoice";
import { getSubscriptionDurationType } from "../../../../helper/getSubscriptionDurationType";
import MainTitle from "../../../../components/ui/MainTitle/MainTitle";
import FormItemDatePicker from "../../../../components/ui/FormItemDatePicker/FormItemDatePicker";
import CustomButton from "../../../../components/ui/CustomButton/CustomButton";
import { useParams } from "react-router-dom";

type ColumnsType<T> = TableProps<T>["columns"];

const ClientPurchaseHistory = () => {
  const { t } = useTranslation();
  const { id } = useParams() as { id: string };
  const formRef = useRef<FormInstance>(null);

  const [filters, setFilters] = useState<{ from: string | null; to: string | null }>({
    from: null,
    to: null,
  });
  const [tableParams, setTableParams] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 10,
  });
  const { data = { data: [], totalCount: 0 }, isLoading } = useGetAllInvoicesQuery({
    currentPage: tableParams.current,
    pageSize: tableParams.pageSize,
    queryOptions: { ...filters, userId: id },
  });

  const totalCount = useMemo(() => {
    if (!data) return 0;
    return data.totalCount;
  }, [data]);

  const handleTableChange: TableProps<IInvoiceForTable>["onChange"] = (pagination) => {
    setTableParams({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  const onFinish = (values: { from: Date | undefined; to: Date | undefined }) => {
    const model = {
      from: values.from ? values.from.toISOString() : null,
      to: values.to ? values.to.toISOString() : null,
    };
    setFilters(model);
  };

  const columns: ColumnsType<IInvoiceForTable> = [
    {
      title: t("User"),
      render: (data: IInvoiceForTable) => {
        return `${data.user.firstname} ${data.user.lastname}`;
      },
    },
    {
      title: t("Subscription"),
      render: (data: IInvoiceForTable) => {
        if (!data.userSubscription) return `-`;
        const { name, duration, durationType, credits } = data.userSubscription.subscription;
        return `${name} / ${duration} ${getSubscriptionDurationType(durationType, duration, t)} / ${credits} ${t("Credits")}`;
      },
    },
    {
      title: t("Addon"),
      render: (data: IInvoiceForTable) => {
        if (!data.addon) return `-`;
        const { credits } = data.addon;
        return `${credits} ${t("Credits")}`;
      },
    },
    {
      title: t("Date"),
      render: (data: IInvoiceForTable) => {
        return `${dayjs(data.createdAt).format("DD/MM/YYYY")}`;
      },
    },
    {
      title: t("TotalPrice"),
      render: (data: IInvoiceForTable) => {
        return `${data.totalPrice}`;
      },
    },
  ];

  return (
    <div className={"gyms-page"}>
      <MainTitle text={t("PurchaseHistory")} />

      <div className={"top-row"}>
        <Form
          className={"form"}
          ref={formRef}
          name="gym-listing-filter"
          onFinish={onFinish}
          autoComplete="off"
          layout={"vertical"}
        >
          <FormItemDatePicker
            dateFormat={"DD/MM/YYYY"}
            name={"from"}
            label={t("From")}
            rules={{ required: false }}
            style={{
              width: "30%",
            }}
          />
          <FormItemDatePicker
            dateFormat={"DD/MM/YYYY"}
            name={"to"}
            label={t("To")}
            rules={{ required: false }}
            style={{
              width: "30%",
            }}
          />
          <div className={"btn-wrapper"}>
            <CustomButton
              text={""}
              type={"submit"}
              typeStyle={"fill"}
              icon={searchIconWhite}
              altIcon={"search"}
            />
          </div>
        </Form>
      </div>

      <Table
        className={"table"}
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={data.data}
        pagination={{ ...tableParams, total: totalCount }}
        loading={isLoading}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default ClientPurchaseHistory;
